import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

export interface TableSort {
  key: string;
  direction: SortDirection;
}

/**
 * Compares two cell values for table sorting:
 * - numbers (and numeric strings, as Supabase returns for numeric columns)
 *   compare numerically
 * - strings compare case-insensitively with numeric awareness ("2" < "10")
 * - booleans compare false < true
 * Empty values (null / undefined / "") always sort last regardless of
 * direction — handled by the hook, not here.
 */
const compareCells = (a: unknown, b: unknown): number => {
  const an = typeof a === "number" ? a : typeof a === "string" && a.trim() !== "" && !Number.isNaN(Number(a)) ? Number(a) : null;
  const bn = typeof b === "number" ? b : typeof b === "string" && b.trim() !== "" && !Number.isNaN(Number(b)) ? Number(b) : null;
  if (an !== null && bn !== null) return an - bn;
  if (typeof a === "boolean" && typeof b === "boolean") return (a ? 1 : 0) - (b ? 1 : 0);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

const isEmpty = (v: unknown): boolean => v === null || v === undefined || v === "";

/**
 * Generic client-side table sorting. Click a column once for ascending,
 * again to flip. `sorted` is a new array; the input is never mutated.
 */
export function useTableSort<T extends object>(
  rows: readonly T[],
  initial: TableSort | null = null
) {
  const [sort, setSort] = useState<TableSort | null>(initial);

  const toggle = (key: string) =>
    setSort((prev) =>
      prev && prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );

  const sorted = useMemo(() => {
    if (!sort) return [...rows];
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((ra, rb) => {
      const a = (ra as Record<string, unknown>)[sort.key];
      const b = (rb as Record<string, unknown>)[sort.key];
      if (isEmpty(a) && isEmpty(b)) return 0;
      if (isEmpty(a)) return 1;
      if (isEmpty(b)) return -1;
      return compareCells(a, b) * dir;
    });
  }, [rows, sort]);

  return { sort, toggle, sorted };
}
